import {Component, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {Subscription} from 'rxjs';

import {
    ApiError,
    LinkedAccountsService,
    CountryModel,
    LinkedAccountModel,
    CompanyExtendedModel,
    CryptoCurrencyInterface,
    IndividualExtendedModel,
    FileStorageService
} from '@spa/api';

import {FormComponent} from '../../../../shared/widgets';
import {FormInterface} from '../../../../shared/interfaces';
import {DialogActionsEnum, DialogComponent, DialogConfigInterface} from '../../../../shared/components';

import {BankAccountsForm} from '../../forms';
import {BankAccountsFormErrorStatesEnum, BankAccountTypesEnum} from '../../enums';

import {AddBankAccountMessages} from '../../../../../assets/messages';

@Component({
    selector: 'app-add-bank-account',
    templateUrl: './add-bank-account.component.html',
    styleUrls: ['./add-bank-account.component.scss']
})
export class AddBankAccountComponent
    extends DialogComponent<AddBankAccountComponent, LinkedAccountModel> implements FormInterface, OnDestroy {

    private registerAccountSubscription: Subscription;
    private accountTypeSubscription: Subscription;

    public bankAccountsForm: BankAccountsForm;
    public createdBankAccount: LinkedAccountModel;
    public isCryptoWallet: boolean;
    public bankAccountTypes: typeof BankAccountTypesEnum;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            client: IndividualExtendedModel | CompanyExtendedModel,
            countries: CountryModel[],
            config: DialogConfigInterface,
            isCorporateClient: boolean,
            cryptoCurrencies: CryptoCurrencyInterface[]
        },
        public dialogRef: MatDialogRef<AddBankAccountComponent>,
        public linkedAccountsService: LinkedAccountsService,
        public fileStorageService: FileStorageService
    ) {
        super(dialogRef);

        this.config = data.config;
        this.isCryptoWallet = false;
        this.bankAccountTypes = BankAccountTypesEnum;

        this.initForm();
    }

    ngOnDestroy(): void {
        this.bankAccountsForm.accountTypeChangedSubscriptions.forEach((subscription: Subscription) => {
            return subscription && subscription.unsubscribe();
        });

        this.accountTypeSubscription?.unsubscribe();

        if (this.registerAccountSubscription) {
            this.registerAccountSubscription.unsubscribe();
        }
    }

    getFormMessages(controlLabel?: string, controlMaxLength?: number): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel, controlMaxLength);
    }

    initForm(): void {
        this.bankAccountsForm = new BankAccountsForm(
            null,
            this.linkedAccountsService,
            this.data.countries,
            this.data.client,
            this.data.isCorporateClient,
            this.fileStorageService,
            this.data.cryptoCurrencies
        );

        this.bankAccountsForm.initForm();

        this.accountTypeSubscription?.unsubscribe();
        this.accountTypeSubscription = this.bankAccountsForm.form
            .get(this.bankAccountsForm.controls.accountType.name)
            .valueChanges
            .subscribe((accountType: number) => {
                this.isCryptoWallet = accountType === BankAccountTypesEnum.CryptoWallet;
            });
    }

    validateForm(): boolean {
        const dataCheck = this.bankAccountsForm.validateForm(this.getFormMessages);
        const fileCheck = this.bankAccountsForm.validateDocumentFile(this.getFormMessages);

        return dataCheck && fileCheck;
    }

    onFormSubmit(action?: number): void {
        if (action !== DialogActionsEnum.Confirm) {
            this.dialogRef.close(this.createdBankAccount);
        } else {
            if (this.validateForm()) {
                this.startLoading(() => {
                    this.registerAccountSubscription = this.bankAccountsForm.createRequest()
                        .subscribe(
                            this.dialogSubscribeHandler.bind(
                                this,
                                (data: LinkedAccountModel) => {
                                    this.createdBankAccount = data;

                                    this.config.process.succeedMessage =
                                        AddBankAccountMessages.BankAccountSuccessfullyCreated.message;
                                },
                                (errors: ApiError[]) => {
                                    this.config.process.failedMessage = errors.map((error: ApiError) => {
                                        switch (error.status) {
                                            case BankAccountsFormErrorStatesEnum.UploadStatementFailed:
                                                return AddBankAccountMessages.UploadStatementFailed.message;
                                            case BankAccountsFormErrorStatesEnum.CreateBankAccountFailed:
                                                return AddBankAccountMessages.CreateBankAccountFailed.message;
                                            case BankAccountsFormErrorStatesEnum.GetBankAccountFailed:
                                                return AddBankAccountMessages.GetBankAccountFailed.message;
                                            default:
                                                return AddBankAccountMessages.UnknownError.message;
                                        }
                                    }).join('<br>');
                                }
                            )
                        );
                });
            }
        }
    }
}
